import { faTag } from '@fortawesome/pro-light-svg-icons';
import { faCircle, faPlusCircle, faTag as faTagSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { ProjectKeywordGq } from '../../../../../graphql/projects/models/ProjectKeywordGq';
import { TagGq } from '../../../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import CellApplyTagPopover from '../../../../../pages/DataManagementPage/TagSection/Dialogs/ApplyTagDialogs/SingleApplyTagDialog';
import VoxelLoadingSpinner from '../../../../voxel/Animation/VoxelLoadingSpinner';
import VoxelDataTableCell from '../../../../voxel/DataTable/VoxelDataTableCell';
import { VoxelPopover } from '../../../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../../voxel/Popover/VoxelPopoverTrigger';
import VoxelTooltipShort from '../../../../voxel/Tooltip/VoxelTooltipShort';
import ColoredTagChip from '../ColoredTagChip';
import { KeywordSelectingGq } from '../../../../../graphql/projects/models/KeywordSelectingGq';
import { KeywordFilteringGq } from '../../../../../graphql/projects/models/KeywordFilteringGq';

export interface TableCellTagGroupProps {
  keyword: ProjectKeywordGq;
  keywordTagIds: number[];
  columnTagGroupId: number;
  tagGroups: Record<number, FastTagGroup>;
  reload: () => Promise<any>;
  tagPanelOpen?: boolean;
  openTagPanel?: (id?: number) => void;
  onTagPopoverOpen: (open: boolean) => void; // Optimization to only pass selection & filtering in case tag is open - these parameters would otherwise break memoization
  backendSelection?: KeywordSelectingGq; // Optimized - only passed if tag popover is open
  backendFiltering?: KeywordFilteringGq; // Optimized - only passed if tag popover is open
  resetSelection?: () => void;
}

export interface FastTagGroup {
  tagGroup: TagGroupGq;
  tags: Record<number, TagGq>;
}

const TableCellTagGroup: React.FC<TableCellTagGroupProps> = React.memo(
  ({
    keyword,
    keywordTagIds,
    columnTagGroupId,
    tagGroups,
    reload,
    tagPanelOpen,
    openTagPanel,
    onTagPopoverOpen,
    backendFiltering,
    backendSelection,
    resetSelection,
  }) => {
    const fastTagGroup = tagGroups[columnTagGroupId];
    const tags = keywordTagIds.map((tagId) => fastTagGroup?.tags[tagId]).filter((tag) => !!tag);
    const tag = tags[0];

    const [loadingSpinner, setLoadingSpinner] = useState(false);

    // Apply tag
    const [localTag, setLocalTag] = useState(tag);
    useEffect(() => setLocalTag(tag), [tag]);

    const [applyTagOpen, setApplyTagOpen] = useState<boolean>(false);
    const singleApplyTagMutation = projectsApiService.mutations.tags.useSingleApplyTag();
    const handleApplyTag = useCallback(
      (tagId: number) => {
        const selectedTag = fastTagGroup.tags[tagId];
        !!selectedTag && setLocalTag(selectedTag);

        return singleApplyTagMutation({
          projectId: fastTagGroup?.tagGroup?.projectId,
          tagId: tagId,
          tagGroupId: fastTagGroup?.tagGroup?.id,
          keywordId: keyword.id,
        })
          .then(() => reload())
          .finally(() => setLoadingSpinner(false));
      },
      [fastTagGroup, backendFiltering, backendSelection]
    );

    const bulkTagMutation = projectsApiService.mutations.tags.useBulkTagKeywords();
    const handleApplySelectionTag = useCallback(
      (tagId: number) => {
        return bulkTagMutation({
          tagId: tagId,
          tagGroupId: fastTagGroup?.tagGroup?.id,
          keywordQuery: {
            filtering: backendFiltering,
            selecting: backendSelection,
          },
        })
          .then(() => reload())
          .finally(() => {
            setLoadingSpinner(false);
            resetSelection();
          });
      },
      [backendFiltering, backendSelection, fastTagGroup]
    );

    useEffect(() => onTagPopoverOpen(applyTagOpen), [applyTagOpen]);

    // Remove tag
    const singleRemoveTagMutation = projectsApiService.mutations.tags.useSingleUntagKeyword();
    const handleRemoveTag = useCallback(() => {
      setLoadingSpinner(true);
      singleRemoveTagMutation({
        projectId: fastTagGroup?.tagGroup?.projectId,
        tagGroupId: fastTagGroup?.tagGroup?.id,
        keywordId: keyword.id,
      })
        .then(() => reload())
        .finally(() => setLoadingSpinner(false));
    }, [fastTagGroup]);

    if (loadingSpinner)
      return (
        <VoxelDataTableCell align={'left'}>
          <div className="w-full flex flex-row justify-start items-center">
            <VoxelLoadingSpinner />
          </div>
        </VoxelDataTableCell>
      );

    return (
      <VoxelDataTableCell key={columnTagGroupId} paddingClass={'py-0 px-2.5'} align={'left'}>
        {localTag && (
          <ColoredTagChip
            key={localTag.id}
            tag={localTag}
            tagGroup={fastTagGroup?.tagGroup}
            onClick={(e) => {
              handleRemoveTag();
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            showClose
            ellipsis
          />
        )}
        {!localTag && (
          <VoxelPopover open={applyTagOpen} onOpenChange={setApplyTagOpen} enableArrow>
            <VoxelTooltipShort message="Tag keyword" disabled={applyTagOpen}>
              <VoxelPopoverTrigger>
                <button
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  className="group w-8 ml-0.5 p-0.5 rounded-[2px]
                  outline outline-1 outline-offset-1 outline-voxel-gray-200 hover:outline-voxel-gray-500 
                   hover:bg-voxel-gray-100 
                   flex flex-row justify-center align-center"
                >
                  <span className="fa-layers fa-fw">
                    <FontAwesomeIcon
                      size={'lg'}
                      icon={faTagSolid}
                      className={`text-voxel-gray-50`}
                    />
                    <FontAwesomeIcon
                      icon={faTag}
                      size={'lg'}
                      className={`text-voxel-gray-300 group-hover:text-voxel-gray-600`}
                    />
                    <FontAwesomeIcon
                      icon={faCircle}
                      size={'sm'}
                      className={`text-white`}
                      transform="right-10"
                    />
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      size={'sm'}
                      className={`text-voxel-gray-300 group-hover:text-success-600/70`}
                      transform="right-10"
                    />
                  </span>
                </button>
              </VoxelPopoverTrigger>
            </VoxelTooltipShort>
            <VoxelPopoverContent>
              <CellApplyTagPopover
                tagPanelOpen={tagPanelOpen}
                openTagPanel={openTagPanel}
                noKeywordsSelected={
                  !backendSelection?.keywordIds?.length && !backendSelection?.invertSelection
                }
                tagGroup={fastTagGroup?.tagGroup}
                handleClose={() => setApplyTagOpen(false)}
                onSuccess={(tagId, selection) =>
                  (selection ? handleApplySelectionTag(tagId) : handleApplyTag(tagId))
                    .then(reload)
                    .finally(() => setApplyTagOpen(false))
                }
              />
            </VoxelPopoverContent>
          </VoxelPopover>
        )}
      </VoxelDataTableCell>
    );
  }
);

export default TableCellTagGroup;
